import React, { useRef, useState, useCallback, useEffect } from "react";
import { ButtonText } from "../../../components";
import Webcam from "react-webcam";
import S from "../styles/StylesWebcam";
import { FaceDetector, FilesetResolver } from "@mediapipe/tasks-vision";

interface CameraTabProps {
  facialFormat?: string;
  cameraRequested: boolean;
  onRequestCamera: () => void;
  onCapture: (imgSrc: { src: string; name: string }) => void;
  onBack: () => void;
}

interface FaceStatus {
  isValid: boolean;
  messages: string[];
}

const INITIAL_FACE_STATUS: FaceStatus = {
  isValid: false,
  messages: ["Posicione seu rosto na área indicada"],
};

function validateFace(
  detections: any[],
  videoWidth: number,
  videoHeight: number
): FaceStatus {
  if (detections.length === 0) {
    return { isValid: false, messages: ["Nenhum rosto detectado"] };
  }

  if (detections.length > 1) {
    return { isValid: false, messages: ["Múltiplos rostos detectados"] };
  }

  const messages: string[] = [];
  let isValid = true;

  const detection = detections[0];
  const boundingBox = detection.boundingBox;
  const keypoints = detection.keypoints;

  const centerX = (boundingBox.originX + boundingBox.width / 2) / videoWidth;
  const centerY = (boundingBox.originY + boundingBox.height / 2) / videoHeight;
  const width = boundingBox.width / videoWidth;

  if (centerX < 0.4) {
    messages.push("Mais para a esquerda");
    isValid = false;
  } else if (centerX > 0.6) {
    messages.push("Mais para a direita");
    isValid = false;
  }

  if (centerY < 0.40) {
    messages.push("Mais para baixo");
    isValid = false;
  } else if (centerY > 0.60) {
    messages.push("Mais para cima");
    isValid = false;
  }

  if (width < 0.35) {
    messages.push("Chegue mais perto da câmera");
    isValid = false;
  } else if (width > 0.45) {
    messages.push("Se afaste da câmera");
    isValid = false;
  }

  if (keypoints && keypoints.length >= 3) {
    const rightEye = keypoints[0];
    const leftEye = keypoints[1];
    const nose = keypoints[2];

    if (rightEye && leftEye && nose) {
      const eyeMidX = (rightEye.x + leftEye.x) / 2;
      const eyeDistance = Math.abs(rightEye.x - leftEye.x);
      const noseToEyeMidX = Math.abs(nose.x - eyeMidX);

      if (eyeDistance > 0 && noseToEyeMidX / eyeDistance > 0.15) {
        messages.push("Olhe reto para a câmera");
        isValid = false;
      }

      const eyeMidY = (rightEye.y + leftEye.y) / 2;
      const noseToEyeDistY = nose.y - eyeMidY;

      if (noseToEyeDistY < eyeDistance * 0.30) {
        messages.push("Abaixe um pouco o queixo");
        isValid = false;
      } else if (noseToEyeDistY > eyeDistance * 0.65) {
        messages.push("Levante um pouco o queixo");
        isValid = false;
      }
    }
  }

  if (isValid) {
    return { isValid: true, messages: ["Perfeito! Aguarde..."] };
  }

  return { isValid, messages };
}

function useFaceDetection(videoElement: HTMLVideoElement | null): FaceStatus {
  const [faceStatus, setFaceStatus] = useState<FaceStatus>(INITIAL_FACE_STATUS);
  const faceDetectorRef = useRef<FaceDetector | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  useEffect(() => {
    if (!videoElement) {
      setFaceStatus(INITIAL_FACE_STATUS);
      return;
    }

    let isActive = true;

    const initFaceDetector = async () => {
      try {
        const basePath = window.location.origin + process.env.PUBLIC_URL;
        const vision = await FilesetResolver.forVisionTasks(
          `${basePath}/mediapipe/wasm`
        );

        const faceDetector = await FaceDetector.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath: `${basePath}/mediapipe/models/blaze_face_short_range.tflite`,
          },
          runningMode: "VIDEO",
          minDetectionConfidence: 0.5,
        });

        if (!isActive) {
          faceDetector.close();
          return;
        }

        faceDetectorRef.current = faceDetector;

        let lastVideoTime = -1;

        const detectFaces = () => {
          if (!isActive || !faceDetectorRef.current) return;

          if (
            videoElement.readyState >= 2 &&
            videoElement.currentTime !== lastVideoTime &&
            videoElement.videoWidth > 0 &&
            videoElement.videoHeight > 0
          ) {
            try {
              const detections = faceDetectorRef.current.detectForVideo(
                videoElement,
                performance.now()
              );
              const status = validateFace(
                detections.detections,
                videoElement.videoWidth,
                videoElement.videoHeight
              );
              setFaceStatus(status);
              lastVideoTime = videoElement.currentTime;
            } catch (error) {
              console.error("Face detection error:", error);
            }
          }

          animationFrameRef.current = requestAnimationFrame(detectFaces);
        };

        detectFaces();
      } catch (error) {
        console.error("Failed to initialize face detector:", error);
      }
    };

    initFaceDetector();

    return () => {
      isActive = false;
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      if (faceDetectorRef.current) {
        faceDetectorRef.current.close();
        faceDetectorRef.current = null;
      }
    };
  }, [videoElement]);

  return faceStatus;
}

function useCountdown(
  start: boolean,
  seconds: number,
  onComplete: () => void
): number | null {
  const [countdown, setCountdown] = useState<number | null>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (start) {
      setCountdown((prev) => (prev === null ? seconds : prev));
    } else {
      setCountdown(null);
      if (countdownRef.current) {
        clearTimeout(countdownRef.current);
        countdownRef.current = null;
      }
    }
  }, [start, seconds]);

  useEffect(() => {
    if (countdown !== null && countdown > 0) {
      countdownRef.current = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    } else if (countdown === 0) {
      onComplete();
    }

    return () => {
      if (countdownRef.current) {
        clearTimeout(countdownRef.current);
      }
    };
  }, [countdown, onComplete]);

  return countdown;
}

function useCapture(
  webcamRef: React.RefObject<(Webcam & { getScreenshot: () => string }) | null>,
  facialFormat: string | undefined,
  onCapture: (imgSrc: { src: string; name: string }) => void
) {
  return useCallback(async () => {
    try {
      const finalCanvas = document.createElement("canvas");
      if (!webcamRef.current?.video) return;
      const finalCtx = finalCanvas.getContext("2d");

      const videoElement = webcamRef.current.video;
      const videoWidth = videoElement.videoWidth;
      const videoHeight = videoElement.videoHeight;

      const targetWidth = 640;
      const targetHeight = 480;
      const targetAspect = targetWidth / targetHeight;
      const videoAspect = videoWidth / videoHeight;

      let sourceX = 0;
      let sourceY = 0;
      let sourceWidth = videoWidth;
      let sourceHeight = videoHeight;

      if (videoAspect > targetAspect) {
        sourceWidth = videoHeight * targetAspect;
        sourceX = (videoWidth - sourceWidth) / 2;
      } else {
        sourceHeight = videoWidth / targetAspect;
        sourceY = (videoHeight - sourceHeight) / 2;
      }

      finalCanvas.width = targetWidth;
      finalCanvas.height = targetHeight;

      if (!finalCtx) return;

      finalCtx.scale(-1, 1);
      finalCtx.translate(-finalCanvas.width, 0);

      finalCtx.drawImage(
        videoElement,
        sourceX, sourceY, sourceWidth, sourceHeight,
        0, 0, targetWidth, targetHeight
      );

      const format = facialFormat === "PNG" ? "image/png" : "image/jpeg";
      onCapture({
        src: finalCanvas.toDataURL(format, 0.95),
        name: "user-photo",
      });
    } catch (error) {
      console.error("Capture error:", error);
    }
  }, [webcamRef, facialFormat, onCapture]);
}

function CameraTab({ facialFormat, cameraRequested, onRequestCamera, onCapture, onBack }: CameraTabProps) {
  const webcamRef = useRef<
    | (Webcam & { getScreenshot: () => string })
    | null
  >(null);
  const [isCameraEnabled, setIsCameraEnabled] = useState(false);
  const feedbackRef = useRef<HTMLSpanElement>(null);
  const countdownTextRef = useRef<HTMLSpanElement>(null);

  const videoElement = isCameraEnabled ? webcamRef.current?.video ?? null : null;
  const faceStatus = useFaceDetection(videoElement);
  const capture = useCapture(webcamRef, facialFormat, onCapture);
  const countdown = useCountdown(faceStatus.isValid, 1, capture);

  useEffect(() => {
    if (feedbackRef.current) {
      feedbackRef.current.textContent =
        (faceStatus.isValid ? "✓ " : "") + faceStatus.messages.join(" • ");
    }
  }, [faceStatus.isValid, faceStatus.messages]);

  useEffect(() => {
    if (countdownTextRef.current) {
      countdownTextRef.current.textContent = countdown != null ? String(countdown) : "";
    }
  }, [countdown]);

  return (
    <S.Container >
      <S.WebcamContent className="webcam-container">
        {cameraRequested && (
          <Webcam
            id="webcam"
            mirrored
            screenshotQuality={1}
            screenshotFormat={facialFormat === "PNG" ? "image/png" : "image/jpeg"}
            videoConstraints={{
              facingMode: "user",
              aspectRatio: 4 / 3,
              width: { ideal: 640 },
              height: { ideal: 480 },
            }}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: isCameraEnabled ? "block" : "none",
            }}
            audio={false}
            ref={webcamRef}
            onUserMedia={() => setIsCameraEnabled(true)}
            onUserMediaError={() => setIsCameraEnabled(false)}
          />
        )}

        {/* Camera disabled overlay — click to activate */}
        <div
          onClick={!isCameraEnabled ? onRequestCamera : undefined}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: isCameraEnabled ? "none" : "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#8D9B9C",
            cursor: cameraRequested ? "default" : "pointer",
          }}
        >
          <S.CameraIcon />
          <span style={{ color: "#fff", marginTop: 8, fontSize: 18, fontWeight: 800, display: cameraRequested ? "none" : "inline" }}>
            Clique para ativar a câmera
          </span>
        </div>

        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "#fff",
            pointerEvents: "none",
            zIndex: 8,
            display: isCameraEnabled ? "block" : "none",
            WebkitMaskImage: "radial-gradient(circle at 50% 50%, transparent 40%, black 36%)",
            maskImage: "radial-gradient(circle at 50% 50%, transparent 40%, black 36%)",
            maskMode: "alpha",
          
          }}
        />

        {/* Face oval overlay */}
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%) translateZ(0)",
            WebkitTransform: "translate(-50%, -50%) translateZ(0)",
            width: "50%",
            height: "66%",
            border: `3px solid ${faceStatus.isValid ? "#4CAF50" : "#f44336"}`,
            borderRadius: "50% / 50%",
            pointerEvents: "none",
            transition: "border-color 0.3s ease",
            zIndex: 10,
            display: isCameraEnabled ? "block" : "none",
          }}
        />

        {/* Countdown overlay */}
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%) translateZ(0)",
            WebkitTransform: "translate(-50%, -50%) translateZ(0)",
            zIndex: 20,
            display: countdown !== null && countdown > 0 ? "flex" : "none",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <S.CountdownNumber translate="no" className="no-translate" ><span translate="no" className="no-translate" ref={countdownTextRef} /></S.CountdownNumber>
        </div>

        {/* Feedback overlay */}
        <S.FeedbackOverlay $isValid={faceStatus.isValid} translate="no" className="no-translate"
          style={{ display: isCameraEnabled ? "flex" : "none" }}>
          <span translate="no" className="no-translate" ref={feedbackRef} />
        </S.FeedbackOverlay>
      </S.WebcamContent>

      <S.Actions>
        <ButtonText onClick={onBack}>
          Voltar
        </ButtonText>
      </S.Actions>
    </S.Container >
  );
}

export default CameraTab;
